import { withNamespaces } from "react-i18next";
import { useState } from "react";
import { Button, Col, Label, Modal, ModalBody, ModalFooter, Row, TabPane } from "reactstrap";
import Accordion from "react-bootstrap/Accordion";
import { useQueries } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import DataProtectionDPIAService from "src/modules/data-protection/apis/DataProtectionDPIAService";
import axios from "axios";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "src/modules/data-protection/constants";
import RiskAssessmentQuestionnaire from "./components/RiskAssessmentQuestionnaire";
import ImpactAssessmentQuestionnaire from "./components/ImpactAssessmentQuestionnaire";
import { mapQuestions } from "src/modules/data-protection/helpers/questionnaire";
import {
    helpers
} from '@smartintegrity/questionnaire';

const DPIAQuestionnaires = ({
    t,
    token,
    onValidSubmit,
    reportCaseId,
    showImpactAssessment
}) => {
    const { assetId } = useParams();

    // Create by AI modal
    const [isCreateAIOpen, setIsCreateAIOpen] = useState(false);
    const [isCreateByAISubmiting, setIsCreateByAISubmiting] = useState(false);

    const sectionsIds = [
        'other_asset_risk_assessment',
        'other_asset_data_protection_impact_assessment'
    ];

    const questionsQueries = useQueries({
        queries: sectionsIds.map((sectionId) => {
            return {
                queryKey: ['data-protection-other-assets-dpia-questions', assetId, sectionId, reportCaseId],
                queryFn: async () => {
                    const service = DataProtectionDPIAService.getInstance();

                    const response = await service.questions(sectionId, {
                        reportCase: reportCaseId,
                        otherAssetId: assetId
                    });
                    
                    const mappedQuestions = mapQuestions(response, response);

                    return {
                        mappedQuestions: helpers.makeQuestionsReadyForQuestionnaire(
                            mappedQuestions,
                            false
                        ),
                        rawQuestions: mappedQuestions,
                        mappedAnswers: helpers.makePreviousAnswersReadyForQuestionnaire(
                            mappedQuestions,
                            mappedQuestions
                        )
                    }
                },
                enabled: !!showImpactAssessment || sectionId !== "other_asset_data_protection_impact_assessment",
                refetchOnWindowFocus: false
            }
        })
    });

    const risksAssessmentQuery = questionsQueries[0];
    const impactAssessmentQuery = questionsQueries[1];

    const createByAI = () => {
        const payload = {
            otherAssetId: Number(assetId),
        };

        setIsCreateByAISubmiting(true);

        axios.post(
            `${API_BASE_URL}/dpia/ai_generate`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        ).then(() => {
            setIsCreateAIOpen(false);
            risksAssessmentQuery.refetch();
        }).catch(() => {
            toast(t("An error occurred while generating by AI."), {
                type: "error",
            });
        }).finally(() => {
            setIsCreateByAISubmiting(false);
        });
    };

    return (
        <TabPane
            tabId="dpia"
            className="pt-4"
        >
            <Modal
                size="md"
                scrollable={true}
                isOpen={isCreateAIOpen}
            >
                <ModalBody>
                    <Label>
                        {t("Would you like to complete the risk assessment using AI suggestions")}?
                    </Label>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        className="waves-effect waves-light"
                        outline
                        type="submit"
                        onClick={() => setIsCreateAIOpen(false)}
                    >
                        {t("No")}
                    </Button>
                    <Button
                        color="primary"
                        className="waves-effect waves-light"
                        type="button"
                        onClick={createByAI}
                    >
                        {isCreateByAISubmiting ? '...' : t("Yes")}
                    </Button>
                </ModalFooter>
            </Modal>

            <Row>
                <Col sm="12">
                    <div className="p-2">
                        <Accordion
                            className="smartintegrity__questionnaire__tabs_progress"
                        >
                            <RiskAssessmentQuestionnaire
                                questionsQuery={risksAssessmentQuery}
                                assetId={assetId}
                                onValidSubmit={onValidSubmit}
                                setIsCreateAIOpen={setIsCreateAIOpen}
                            />

                            <ImpactAssessmentQuestionnaire
                                questionsQuery={impactAssessmentQuery}
                                assetId={assetId}
                                onValidSubmit={onValidSubmit}
                                showImpactAssessment={showImpactAssessment}
                            />
                        </Accordion>
                    </div>
                </Col>
            </Row>
        </TabPane>
    );
};

const mapStatetoProps = (state) => {
    const { token } = state.Login;

    return {
        token
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        DPIAQuestionnaires
    )
);
